import type { backendOld } from "~/api";
import { getMediaTypeFromFilename, getResolvedFileMediaType, MEDIA_TYPE_OCTET_STREAM } from "~/constants/mimeType";
import { isDefined } from "~/shared/common";
import { PresignedUrlKind } from "~/shared/enums";
import type { Attachment, AttachmentCreate, AttachmentUpdate } from "~/shared/types";
import { truncateFilename } from "~/utils/api";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid } from "~/utils/common";
import { attachmentComparator } from "~/utils/comparator";
import { printOrCaptureMessage } from "~/utils/exceptionClient";
import {
  finishFileUpload,
  makeFileUploadProgressTracker,
  partitionReadableFiles,
  startFileUpload,
} from "~/utils/fileUpload";
import { isImageType } from "~/utils/media";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

const getPaginationKey = (taskDuid: string) => `attachments:${taskDuid}`;

type CreateAttachmentOptions = {
  taskDuid?: string;
  formDuid?: string;
  noBackend?: boolean;
  awaitBackend?: boolean;
};

export type State = {
  _duidsToAttachments: Map<string, Attachment>;
};

export const state: State = {
  _duidsToAttachments: new Map(),
};

export type Getters = {
  getAttachmentByDuid: (duid: string) => Promise<Attachment | undefined>;
  getAttachmentByDuidIfLoaded: (duid: string, options?: { assertLoaded?: boolean }) => Attachment | undefined;
  getAttachmentsByDuidsOrdered: (duids: string[]) => Promise<Attachment[]>;
  getAttachmentsByDuidsOrderedIfLoaded: (duids: string[]) => Attachment[];
  getLoadedAttachmentsByTaskDuidOrdered: (taskDuid: string) => Attachment[];
  getCoverImageAttachmentByTaskDuid: (taskDuid: string) => Promise<Attachment | null>;
};

export type Actions = {
  loadAttachments: (
    ...args: Parameters<typeof backendOld.entityList.attachment>
  ) => ReturnType<typeof backendOld.entityList.attachment>;
  loadPaginatedAttachmentsForTaskDuid: (taskDuid: string) => Promise<number>;
  createAttachment: (
    file: File,
    order: string,
    partialAttachment?: Partial<Attachment>,
    options?: CreateAttachmentOptions
  ) => Promise<{ attachment: Attachment; create: AttachmentCreate }>;
  createAttachments: (
    attachmentConfigs: {
      file: File;
      order: string;
      partialAttachment?: Partial<Attachment>;
    }[],
    options?: CreateAttachmentOptions
  ) => Promise<{ attachment: Attachment; create: AttachmentCreate }[]>;
  updateAttachment: (update: AttachmentUpdate, options?: { awaitBackend?: boolean }) => Promise<Attachment | undefined>;
  deleteAttachment: (attachment: Attachment, options?: { awaitBackend?: boolean }) => Promise<void>;
  createAttachmentFromUrl: (url: string, name: string) => string;
  $createOrUpdateAttachment: (attachment: Attachment) => void;
  $deleteAttachment: (attachment: Attachment) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getAttachmentByDuid() {
    return async (duid) => {
      const attachment = this.getAttachmentByDuidIfLoaded(duid);
      if (attachment) {
        return attachment;
      }

      if (this.$isEntityDuidBlacklisted(duid)) {
        return undefined;
      }

      const attachmentFetched = await this.$batchers.entityListManager.getEntityByDuid("attachments", duid);
      if (!attachmentFetched) {
        this.$blacklistEntityDuid("attachments", duid);
        return undefined;
      }

      this.$createOrUpdateAttachment(attachmentFetched);
      return attachmentFetched;
    };
  },
  getAttachmentByDuidIfLoaded() {
    return (duid, options = {}) => {
      const { assertLoaded = false } = options;
      const attachment = this._duidsToAttachments.get(duid);
      if (assertLoaded && !attachment) {
        printOrCaptureMessage("Assert loaded attachment with duid not found", { duid });
      }
      return attachment;
    };
  },
  getAttachmentsByDuidsOrdered() {
    return async (duids) => {
      const attachments = await Promise.all(duids.map((e) => this.getAttachmentByDuid(e)));
      return attachments.filter(isDefined).sort(attachmentComparator);
    };
  },
  getAttachmentsByDuidsOrderedIfLoaded() {
    return (duids) => {
      const attachments = duids.map((e) => this.getAttachmentByDuidIfLoaded(e));
      return attachments.filter(isDefined).sort(attachmentComparator);
    };
  },
  getLoadedAttachmentsByTaskDuidOrdered() {
    return (taskDuid) => {
      const paginationState = this.$getPaginationState(getPaginationKey(taskDuid), null);

      if (!paginationState) {
        return [];
      }

      const attachments = [...paginationState.duids.values()]
        .map((e) => this.getAttachmentByDuidIfLoaded(e))
        .filter(isDefined);
      return attachments;
    };
  },
  getCoverImageAttachmentByTaskDuid() {
    return async (taskDuid) => {
      const task = this.getTaskByDuidIfLoaded(taskDuid);
      if (!task) {
        return null;
      }

      const loadedImageAttachment =
        this.getAttachmentsByDuidsOrderedIfLoaded(task.attachmentDuids).find((e) => isImageType(e.kind)) ?? null;
      if (loadedImageAttachment) {
        return loadedImageAttachment;
      }

      if (task.attachmentDuids.length === 0) {
        return null;
      }

      const areAllAttachmentsLoaded = task.attachmentDuids.every(
        (e) => this.getAttachmentByDuidIfLoaded(e) !== undefined
      );
      if (areAllAttachmentsLoaded) {
        return null;
      }

      const attachmentFetched = await this.$batchers.taskCoverImageManager.getCoverImageAttachmentForTask(taskDuid);
      if (attachmentFetched) {
        this.$createOrUpdateAttachment(attachmentFetched);
      }
      return attachmentFetched;
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadAttachments(...args) {
    const response = await this.$backendOld.entityList.attachment(...args);
    response.data.results.forEach((attachment: Attachment) => {
      if (this.getAttachmentByDuidIfLoaded(attachment.duid)) {
        return;
      }
      this.$createOrUpdateAttachment(attachment);
    });
    return response;
  },
  async loadPaginatedAttachmentsForTaskDuid(taskDuid) {
    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return 0;
    }

    // If the task has no attachments, we don't need to load any
    if (task.attachmentDuids.length === 0) {
      this.$createPaginationState(getPaginationKey(taskDuid), null, attachmentComparator);
      return 0;
    }

    const paginationState = this.$getPaginationState(getPaginationKey(taskDuid), null);
    const { data } = await this.loadAttachments(taskDuid, paginationState?.duids.size ?? 0);
    if (!paginationState) {
      this.$createPaginationState(getPaginationKey(taskDuid), null, attachmentComparator);
    }
    this.$addEntitiesToPaginationState(getPaginationKey(taskDuid), null, data.results, data.count);

    return data.count;
  },
  async createAttachment(file, order, partialAttachment, options = {}) {
    const { taskDuid, formDuid, noBackend = false, awaitBackend = false } = options;
    const attachmentDuid = partialAttachment?.duid ?? makeDuid();
    startFileUpload(attachmentDuid, file);
    const { data } = await this.$backendOld.createPresignedUrl(
      PresignedUrlKind.ATTACHMENT,
      file.name,
      attachmentDuid,
      formDuid
    );

    const mimeType = getResolvedFileMediaType(file);
    const attachment: Attachment = {
      duid: attachmentDuid,
      name: truncateFilename(file.name, 150),
      kind: mimeType,
      order,
      fileUrl: data.fileUrl,
      colorHex: makeRandomColorHex(),
      recommendationDuid: null,
      ...partialAttachment,
    };

    const attachmentCreates: AttachmentCreate[] = [{ ...attachment, filePath: data.filePath }];

    this.$createOrUpdateAttachment(attachment);

    if (taskDuid) {
      this.$addEntitiesToPaginationStates(getPaginationKey(taskDuid), [attachment]);
    }

    if (!noBackend) {
      const backendAction = this.$backend.attachment.createMany(attachmentCreates);
      if (awaitBackend) {
        await backendAction;
      }
    }

    // TODO refactor to remove this timeout--can do this by returning the code that happens in the timeout as a callback and calling that after the attachment is created
    // the reason that this is this way right now is that we need this code to run after the actual attachment create and setTimeout basically puts it on the next tick
    // and we we were too lazy to refactor it right now
    // oxlint-disable-next-line dart/no-set-timeout
    setTimeout(async () => {
      await this.$backendOld._put(
        data.presignedUrl,
        file,
        { authOverride: true, absolute: true, nonBlocking: true, background: true },
        data.signedHeaders,
        makeFileUploadProgressTracker(attachmentDuid)
      );
      finishFileUpload(attachmentDuid);
    });

    return { attachment, create: attachmentCreates[0] };
  },
  async createAttachments(attachmentConfigs, options) {
    const { readableFiles } = await partitionReadableFiles(attachmentConfigs);

    if (readableFiles.length === 0) {
      return [];
    }

    return Promise.all(
      readableFiles.map(async ({ file, order, partialAttachment }) =>
        this.createAttachment(file, order, partialAttachment, options)
      )
    );
  },
  async updateAttachment(update, options = {}) {
    const { awaitBackend = false } = options;
    const attachment = this.getAttachmentByDuidIfLoaded(update.duid);
    if (!attachment) {
      return undefined;
    }

    Object.assign(attachment, update);

    const backendAction = this.$backend.attachment.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return attachment;
  },
  async deleteAttachment(attachment, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteAttachment(attachment);

    this.$addEntityDuidPendingDeletion(attachment.duid);
    const backendAction = this.$backend.attachment.delete(attachment.duid);
    const action = (async () => {
      await backendAction;
      this.$removeEntityDuidPendingDeletion(attachment.duid);
    })();

    if (awaitBackend) {
      await action;
    }
  },
  createAttachmentFromUrl(url, name) {
    const attachmentDuid = makeDuid();
    const normalizedUrl = url.trim().replace(/^<|>$/g, "");
    let pathname = normalizedUrl.split(/[?#]/, 1)[0] ?? normalizedUrl;
    try {
      ({ pathname } = new URL(normalizedUrl));
    } catch {
      // Ignore invalid or non-absolute URLs; keep best-effort extension detection from raw input.
    }
    const mimeType = getMediaTypeFromFilename(pathname) ?? MEDIA_TYPE_OCTET_STREAM;

    const attachment: Attachment = {
      duid: attachmentDuid,
      name: truncateFilename(name, 150),
      kind: mimeType,
      order: Date.now().toString(),
      fileUrl: normalizedUrl,
      colorHex: makeRandomColorHex(),
      recommendationDuid: null,
    };
    this.$createOrUpdateAttachment(attachment);
    return attachmentDuid;
  },
  $createOrUpdateAttachment(attachment) {
    const currentAttachment = this.getAttachmentByDuidIfLoaded(attachment.duid);
    if (this.$isEntityDuidPendingDeletion(attachment.duid)) {
      return;
    }

    if (!currentAttachment) {
      this._duidsToAttachments.set(attachment.duid, attachment);
    } else {
      Object.assign(currentAttachment, attachment);
    }

    this.$unblacklistEntityDuid(attachment.duid);
  },
  $deleteAttachment(attachment) {
    this._duidsToAttachments.delete(attachment.duid);
    this.$removeEntitiesFromPaginationStates([attachment]);
  },
};

export { actions, getters };
